import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../shared/providers/LanguageProvider';
import offerService from '../services/offerService';
import { fetchMerchants } from '../services/merchantService';
import productService from '../services/productService';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const initialForm = {
  merchant_id: '',
  offer_title: '',
  offer_description: '',
  discount_percentage: '',
  valid_from: '',
  valid_until: '',
  max_uses: '',
  min_wallet_balance: '',
  status: 'active',
};

const OfferCreate = () => {
  const { token } = useAuth();
  const { t } = useLanguage();

  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState([]);

  useEffect(() => {
    // load merchants
    fetchMerchants(token).then(setMerchants);
  }, [token]);

  useEffect(() => {
    // fetch products when merchant changes
    async function fetchProductsForMerchant(merchantId) {
      if (!merchantId) { setProducts([]); return; }
      try {
        const all = await productService.getProducts(token);
        const list = Array.isArray(all) ? all : (all?.data || []);
        const filtered = list.filter(p => (p.merchant_id || p.merchantId) === merchantId);
        setProducts(filtered);
      } catch (e) {
        setProducts([]);
      }
    }
    fetchProductsForMerchant(form.merchant_id);
  }, [form.merchant_id, token]);

  const canSubmit = useMemo(() => {
    return form.merchant_id && form.offer_title && form.discount_percentage && form.valid_from && form.valid_until && selectedProductIds.length > 0;
  }, [form, selectedProductIds]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const toggleProduct = (id) => {
    setSelectedProductIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!canSubmit) return;
    setLoading(true);
    try {
      const baseOffer = {
        merchant_id: form.merchant_id,
        offer_title: form.offer_title,
        offer_description: form.offer_description || undefined,
        discount_percentage: parseFloat(form.discount_percentage),
        valid_from: new Date(form.valid_from).toISOString(),
        valid_until: new Date(form.valid_until).toISOString(),
        max_uses: form.max_uses ? parseInt(form.max_uses, 10) : undefined,
        min_wallet_balance: form.min_wallet_balance ? parseFloat(form.min_wallet_balance) : undefined,
        status: form.status,
      };

      if (selectedProductIds.length === 1) {
        // single
        await offerService.addOffer(token, { ...baseOffer, product_id: selectedProductIds[0] });
        setSuccess('Offer created successfully');
      } else {
        // multiple
        await offerService.addOffer(token, { ...baseOffer, product_ids: selectedProductIds });
        setSuccess(`Created ${selectedProductIds.length} offers`);
      }
      setForm(initialForm);
      setSelectedProductIds([]);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to create offer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={`${t('offers') || 'Offers'} — ${t('create') || 'Create'}`}
      />

      {error && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-2 text-sm text-destructive">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-4 py-2 text-sm text-emerald-700 dark:text-emerald-400">
          {success}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t('create') || 'Create'} {t('offers') || 'Offer'}</CardTitle>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>{t('merchants') || 'Merchants'}</Label>
              <Select
                value={form.merchant_id || undefined}
                onValueChange={(value) => setForm(prev => ({ ...prev, merchant_id: value }))}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder={`${t('select') || 'Select'}...`} />
                </SelectTrigger>
                <SelectContent>
                  {merchants.map(m => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.business_name} ({m.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="offer_title">{t('title') || 'Title'}</Label>
                <Input
                  id="offer_title"
                  name="offer_title"
                  value={form.offer_title}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="discount_percentage">{t('discountPercent') || 'Discount %'}</Label>
                <Input
                  id="discount_percentage"
                  name="discount_percentage"
                  type="number"
                  min="0.01"
                  max="100"
                  step="0.01"
                  value={form.discount_percentage}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="offer_description">{t('description') || 'Description'}</Label>
              <Textarea
                id="offer_description"
                name="offer_description"
                value={form.offer_description}
                onChange={handleChange}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="valid_from">{t('validFrom') || 'Valid From'}</Label>
                <Input
                  id="valid_from"
                  name="valid_from"
                  type="date"
                  value={form.valid_from}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="valid_until">{t('validUntil') || 'Valid Until'}</Label>
                <Input
                  id="valid_until"
                  name="valid_until"
                  type="date"
                  value={form.valid_until}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="max_uses">{t('maxUses') || 'Max Uses'}</Label>
                <Input
                  id="max_uses"
                  name="max_uses"
                  type="number"
                  min="1"
                  value={form.max_uses}
                  onChange={handleChange}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="min_wallet_balance">{t('minWalletBalance') || 'Min Wallet Balance'}</Label>
                <Input
                  id="min_wallet_balance"
                  name="min_wallet_balance"
                  type="number"
                  min="0"
                  value={form.min_wallet_balance}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('products') || 'Products'} ({products.length})</Label>
              <div className="max-h-64 overflow-y-auto rounded-md border border-input p-3">
                {products.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {form.merchant_id
                      ? (t('noProductsAvailable') || 'No products available')
                      : `${t('select') || 'Select'} ${t('merchants') || 'Merchants'}`}
                  </p>
                ) : (
                  products.map(p => (
                    <label key={p.id} className="flex cursor-pointer items-center gap-2 py-1">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-input accent-primary"
                        checked={selectedProductIds.includes(p.id)}
                        onChange={() => toggleProduct(p.id)}
                      />
                      <span className="text-sm text-foreground">
                        {p.product_name || p.name} — {p.original_price ? `SAR ${p.original_price}` : ''}
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>
          </CardContent>
          <CardFooter className="justify-end">
            <Button type="submit" disabled={!canSubmit || loading}>
              {loading ? (t('creating') || 'Creating...') : (t('create') || 'Create')}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default OfferCreate;
